import moment from 'moment';
import { formatBuddhistEra } from 'src/app/shared/pipes/thai-datetime.pipe';
import { bahttext } from 'bahttext';
import {
    map as _map,
    round as _round,
    sumBy as _sumBy,
    toNumber as _toNumber,
    toString as _toString,
    trim as _trim,
} from 'lodash';
import {
    ReceiptNoteItem,
    ReceiptNoteParameter,
} from 'src/app/shared/services/print-sheet/interfaces/receipt-note-param';
import { StoreConfig } from 'src/app/shared/config/store-config';
import { SellerEntity } from 'src/app/shared/config/entity-config';
import {
    ReceiptItemInfo,
    ReceiptNoteInput,
    inputMomentFormat,
} from './receipt-note-input';

// The builder that turns the receipt form state into the jsreport
// `receipt-note` page. Pure so the spec beside this file can pin the built
// object — in particular the totals arithmetic and the rule that the seller's
// tax registration appears only when the customer supplied their own.

// Thai Buddhist-era date in the long form the template's sample data uses,
// e.g. 20 สิงหาคม 2569.
export const formatReceiptDate = (input: string): string => {
    if (input) {
        const momentObj = moment(input, inputMomentFormat);
        if (momentObj.isValid()) {
            return formatBuddhistEra(momentObj, 'D MMMM YYYY');
        }
    }
    return '';
};

const formatString = (input: unknown): string => {
    return _trim(_toString(input));
};

// Collapses runs of whitespace so the remark prints on a single line.
const formatRemark = (input: unknown): string => {
    return _trim(_toString(input).replace(/\s+/g, ' '));
};

const toAmount = (value: unknown): number => {
    const num = _toNumber(value);
    return Number.isFinite(num) ? _round(num, 2) : 0;
};

// Sum of the item rows. Exported because the form shows the same figures the
// document will carry — computed once, here, not twice.
export const itemTotal = (items: ReceiptItemInfo[]): number => {
    return _round(
        _sumBy(items, (item) => toAmount(item.amount)),
        2,
    );
};

export const netPrice = (input: ReceiptNoteInput): number => {
    return _round(
        itemTotal(input.items) -
            toAmount(input.amounts.discount) +
            toAmount(input.amounts.shippingCost),
        2,
    );
};

// `seller` is the taxpayer whose sale the order is (sellerFor at the order
// date). Its tax id is printed only when the customer supplied one of their
// own — that is what turns the receipt into a tax receipt, and the seller's
// registration must then be on it; a receipt without a customer tax id prints
// the store's brand alone, exactly like the plain invoice.
export const buildReceiptNoteParam = (
    input: ReceiptNoteInput,
    store: StoreConfig,
    seller: SellerEntity,
): ReceiptNoteParameter => {
    const customerTaxId = formatString(input.customerInfo.taxId);
    const customerBranch = formatString(input.customerInfo.branch);
    const total = itemTotal(input.items);
    const net = netPrice(input);
    const items: ReceiptNoteItem[] = _map(
        input.items,
        (item: ReceiptItemInfo, index: number): ReceiptNoteItem => ({
            itemNo: index + 1,
            description: formatString(item.description),
            unitPrice: toAmount(item.unitPrice),
            quantity: toAmount(item.quantity),
            amount: toAmount(item.amount),
        }),
    );
    return {
        sellerInfo: {
            name: store.printInfo.name,
            website: store.printInfo.website,
            address: store.printInfo.address,
            ...(customerTaxId
                ? { taxId: seller.taxId, branch: seller.branch }
                : {}),
        },
        customerInfo: {
            name: formatString(input.customerInfo.name),
            address: formatString(input.customerInfo.address),
            ...(customerTaxId ? { taxId: customerTaxId } : {}),
            ...(customerTaxId && customerBranch
                ? { branch: customerBranch }
                : {}),
        },
        orderInfo: {
            // Blank when the operator left the date for the customer to
            // write in: the template prints the วันที่ label either way, so
            // an empty string is the blank they fill.
            date: input.docInfo.hideDate
                ? ''
                : formatReceiptDate(input.docInfo.date),
            orderNo: formatString(input.source.orderNo),
            total,
            discount: toAmount(input.amounts.discount),
            shippingCost: toAmount(input.amounts.shippingCost),
            netPrice: net,
            netPriceInWords: bahttext(net),
            remark: formatRemark(input.docInfo.remark),
        },
        docInfo: {
            title1: formatString(input.docInfo.customTitle),
            hideLogo: !!input.docInfo.hideLogo,
            showSellerSignature: !!input.docInfo.showSellerSignature,
        },
        items,
    };
};
